import { ChevronRightIcon, UserIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/20/solid';

import { ShiftCard as ShiftCardData } from '../api/types';
import { shiftIdentity } from '../util/identity';
import { formatTimeRange } from './ShiftCard';

// One shift as a full-width row in the Day view (GRAVY-10329).
//
// Figma 39391:14056 → `Item 1` (Critical), `Item 3` (At risk), `Item 4`
// (Filled): a bordered card, 16px padding, 24px between its header and the
// hired pill. The header is client → skill → time → location with the badge and
// a chevron pinned right; the text column is `flex: 1` with `min-width: 0` so a
// long client name ellipsises instead of pushing the chevron off.
//
// ## THE WHOLE CARD is the priority signal, not a strip inside it
//
// GRAVY-10335: this used to tint a small box around the fraction and treat
// Critical and At risk identically — same red strip, same "Needs attention" —
// so an admin scanning the day could not tell an imminent unfilled shift from
// one that is merely underfilled. That is the entire point of the Critical /
// At-risk split, and it was invisible.
//
// The card now carries the state:
//
//   Critical  red border, faint red wash, "Needs attention"
//   At risk   amber border, faint amber wash, NO badge
//   Filled    the neutral card, no badge
//
// "Needs attention" is RESERVED for Critical. It is the same badge, and the same
// reservation, as the Shift Detail panel's (GRAVY-10328) — both read the
// server's `fill_status`, so a red card, its badge and the panel it opens cannot
// disagree.
//
// ## The hired pill is coloured by a rule of its own
//
// Deliberately not just "the card's colour again": a shift with NOBODY hired is
// red even when the card is amber, because zero staffed is the fact an admin
// acts on regardless of when the shift starts. So —
//
//   filled           green
//   0 of N           red    (whatever the band)
//   partial Critical red
//   partial At risk  amber
//
// ## What is NOT recomputed here
//
// The band is `fill_status` from the server — the same value the week grid
// colours by and the summary pills filter on. The 24h Critical window and the
// hired >= quantity test live in Dashboard::ShiftClassification and are shared
// with Shift Detail; re-deriving either here is what let the two views drift in
// the first place. The only thing read locally is `hired_count === 0`, which is
// a property of the number being displayed, not a timing rule.

type Props = {
  shift: ShiftCardData;
  onOpen: (shiftId: number) => void;
};

/**
 * Which tone the hired fraction wears. Separate from the card's band on
 * purpose — see the rule above.
 *
 * `unknown` is the honest answer when an older API sends no `fill_status`: the
 * pill renders neutral rather than guessing a colour that would be a claim
 * about urgency nobody made.
 */
const metricTone = (
  band: string | null | undefined,
  hired: number,
): 'filled' | 'empty' | 'critical' | 'atrisk' | 'unknown' => {
  if (band === 'filled') return 'filled';
  if (band !== 'critical' && band !== 'at_risk') return 'unknown';
  if (hired === 0) return 'empty';
  return band === 'critical' ? 'critical' : 'atrisk';
};

/** Identity and the time range are shared with the WEEK card and the Shift
    Detail panel, deliberately: the same shift must read identically wherever it
    appears (GRAVY-10290, GRAVY-10340). */
const identity = shiftIdentity;

/** "DC 20001" — the location identifier the design shows. */
const location = (s: ShiftCardData) =>
  [s.state, s.zip].filter(Boolean).join(' ') || s.location_code || '';

const DayShiftCard = ({ shift, onOpen }: Props) => {
  const band = shift.fill_status;
  // RESERVED for Critical (GRAVY-10335). It used to include at_risk, which made
  // every underfilled shift on the day look equally urgent.
  const needsAttention = band === 'critical';
  const tone = metricTone(band, shift.hired_count ?? 0);

  return (
    // THE WHOLE CARD opens the panel (GRAVY-10342). It used to be the chevron
    // alone — a 24px target on a card the width of the page, which reads as
    // clickable everywhere and responds in one corner. The week grid's card has
    // always been a single button; this makes the Day view match.
    //
    // One control, not a clickable div with a button inside it: nested buttons
    // are invalid, and two overlapping targets doing the same thing is what a
    // screen reader would announce twice. The chevron stays as the affordance,
    // now decorative — the card carries the accessible name.
    <button
      type="button"
      className={`dashv2-daycard is-${band ?? 'unknown'}`}
      id={`dashv2-daycard-${shift.id}`}
      data-testid="dayShiftCard"
      data-fill-status={band ?? ''}
      aria-label={`Open shift details for ${identity(shift)}`}
      onClick={() => onOpen(shift.id)}
    >
      {/* The button's layout lives in THIS wrapper, not on the button itself
          (GRAVY-10351). WebKit wraps a <button>'s children in an anonymous box
          that does not stretch, so `display: flex` applied directly to the
          button leaves the rows shrink-wrapped — which is how the badge and the
          chevron ended up beside the text instead of pinned to the card's right
          edge in Safari, while Chromium laid them out correctly.

          Keeping the <button> keeps GRAVY-10342's single accessible control;
          the wrapper simply gives the flex layout a normal block to live in. */}
      <div className="dashv2-daycard-inner">
        <div className="dashv2-daycard-head">
          <div className="dashv2-daycard-main">
            <p className="dashv2-daycard-title" title={identity(shift)}>
              {/* A restricted-pool job is locked in the week grid too; the icon
                travels with the identity rather than being a separate column. */}
              {shift.restricted_pool && (
                <LockClosedIcon
                  className="dashv2-daycard-lock"
                  aria-label="Restricted pool"
                />
              )}
              {identity(shift)}
            </p>
            <p className="dashv2-daycard-role">{shift.skill_name}</p>
            <p className="dashv2-daycard-time">{formatTimeRange(shift)}</p>
            <p className="dashv2-daycard-loc">{location(shift)}</p>
          </div>

          {needsAttention && (
            <span className="dashv2-daycard-badge">Needs attention</span>
          )}

          {/* Decorative now — the card itself is the control, so this must not
            be a second focus stop or a second thing announced. */}
          <span className="dashv2-daycard-open" aria-hidden="true">
            <ChevronRightIcon />
          </span>
        </div>

        {/* The pill is the fraction, and its own colour. No surrounding tinted
          strip any more: the CARD carries the urgency now, and a second tinted
          box inside a tinted card said the same thing twice while making
          Critical and At risk look identical (GRAVY-10335). */}
        <div className="dashv2-daycard-fill">
          <span
            className={`dashv2-daycard-metric is-${tone}`}
            data-tone={tone}
            // Read as one fact by a screen reader; the icon alone says nothing,
            // and colour is not information an assistive tech can convey.
            aria-label={`${shift.hired_count} of ${shift.quantity} hired`}
          >
            <UserIcon aria-hidden="true" />
            {/* Scales past 1 — the fraction is printed, never a boolean. */}
            {shift.hired_count}/{shift.quantity} hired
          </span>
        </div>
      </div>
    </button>
  );
};

export default DayShiftCard;
